import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { cloneDeep } from 'lodash';

import Loading from 'components/Loading/Loading';
import { pushNotificationWithTimeout } from 'modules/notifications/notificationsSlice';

import { Box } from '@chakra-ui/react';

import { useGetCompanyParamsQuery } from 'modules/params/companyParamsApi';

import { useGetMruVariableKeysQuery } from 'modules/templates/templatesApi';

function MRUMenu({ insertKey, closeMenu, exportKeys = [] }) {
  const dispatch = useDispatch();

  const [companyParams, setCompanyParams] = useState();

  const {
    data: mruVariableKeysData,
    isLoading: mruVariableKeysIsLoading,
    error: mruVariableKeysErrorc,
  } = useGetMruVariableKeysQuery();

  const cloneMruVariableKeys = useMemo(
    () => cloneDeep(mruVariableKeysData),
    [mruVariableKeysData]
  );

  const {
    data: companyParamsData,
    isLoading: companyParamsIsLoading,
    error: companyParamsError,
  } = useGetCompanyParamsQuery({
    scopes: 'template',
  });

  if (mruVariableKeysErrorc) {
    dispatch(
      pushNotificationWithTimeout({
        type: 'error',
        message: 'Failed to fetch mru variable keys data',
      })
    );
  }

  if (companyParamsError) {
    dispatch(
      pushNotificationWithTimeout({
        type: 'error',
        message: 'Failed to fetch params data',
      })
    );
  }

  useEffect(() => {
    if (companyParamsData) {
      const params = cloneDeep(companyParamsData)?.data.entities.map(cp => ({
        ...cp,
        scopes: cp.scopes.length === 2 ? ['all'] : cp.scopes,
      }));
      setCompanyParams(params);
    }
  }, [companyParamsData]);

  const handleClick = key => {
    insertKey(key);
    closeMenu();
  };

  if (mruVariableKeysIsLoading || companyParamsIsLoading) {
    return <Loading />;
  }

  return (
    <Box data-testid="mru-edit-token">
      <Box
        sx={{
          fontWeight: 700,
          fontSize: '14px',
          paddingBottom: '1rem',
        }}
      >
        Edit Personalization Token
      </Box>
      <Box>
        {exportKeys.length > 0 && (
          <Box>
            <Box sx={{ fontSize: '14px', fontWeight: 500 }}>
              Promo Code Expiry
            </Box>
            <Box>
              {exportKeys.map((exportKey, index) => (
                <Box
                  data-testid={`mru-export-key-${index}`}
                  key={exportKey}
                  // insert the whole mustache expression, as the recently
                  // used tokens do. insertKey swallows a "{{" the designer has
                  // already typed, and the braces are what makes this a token
                  // rather than literal text.
                  onClick={() => handleClick(`{{${exportKey}}}`)}
                  sx={{
                    height: '2.125rem',
                    fontSize: '14px',
                    padding: '0 1rem',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    ':hover': {
                      background: '#f5f5f5',
                    },
                  }}
                >
                  {exportKey}
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {cloneMruVariableKeys && cloneMruVariableKeys.length > 0 && (
          <Box>
            <Box sx={{ fontSize: '14px', fontWeight: 500 }}>
              Recently Used Tokens
            </Box>
            <Box>
              {cloneMruVariableKeys.slice(0, 5).map((option, index) => (
                <Box
                  data-testid={`mru-edit-token-${index}`}
                  key={option.value}
                  onClick={() => {
                    handleClick(option.value);
                  }}
                  sx={{
                    height: '2.125rem',
                    fontSize: '14px',
                    padding: '0 1rem',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    ':hover': {
                      background: '#f5f5f5',
                    },
                  }}
                >
                  {option.name}
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {companyParams && !!companyParams.length && (
          <Box>
            <Box sx={{ fontSize: '14px', fontWeight: 500 }}>Configured</Box>
            <Box>
              {companyParams.map(option => (
                <Box
                  key={option.id}
                  // a company param's key is a bare name, so wrap it as the
                  // recently used tokens already arrive wrapped. Without the
                  // braces the insert is literal text: it is not styled as a
                  // token and never reaches the var's keys, which the server
                  // rejects on save.
                  onClick={() => handleClick(`{{${option.key}}}`)}
                  sx={{
                    height: '2.125rem',
                    fontSize: '14px',
                    padding: '0 1rem',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    ':hover': {
                      background: '#f5f5f5',
                    },
                  }}
                >
                  {option.label}
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
}
export default MRUMenu;
